import React, { ForwardedRef } from 'react'

import classNames from 'classnames'

import FieldError from '@components/shared/form/FieldError'

type Props = (
  | React.InputHTMLAttributes<HTMLInputElement>
  | React.TextareaHTMLAttributes<HTMLTextAreaElement>
) & {
  name: string
  error?: string | null
  label?: string | JSX.Element
  variant?: 'sm' | 'base'
  multiline?: boolean
  inputClassName?: string
  solidBackground?: boolean
}

const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, Props>(
  (
    {
      variant,
      name,
      id,
      error,
      label,
      multiline,
      className,
      inputClassName,
      solidBackground,
      ...props
    }: Props,
    ref
  ) => {
    const classes = classNames(
      'rounded-sm border mb-2 w-full text-md focus:border-gray-text dark:focus:border-gray outline-none',
      {
        'border-orange': error,
        'px-4 py-3': variant === 'base',
        'px-2.5 py-1.5': variant === 'sm',
        'cursor-not-allowed border-gray-border dark:border-gray-border-dm bg-gray-light':
          props.disabled,
        'bg-transparent': !props.disabled && !solidBackground,
        'bg-white dark:bg-black-bg2': !props.disabled && solidBackground,
        'border-gray dark:border-gray-border-dm': !error && !props.disabled,
      },
      inputClassName
    )
    return (
      <div className={classNames('flex flex-col', className)}>
        {label && (
          <label className="mb-2 w-full text-md" htmlFor={id ?? name}>
            {label}
          </label>
        )}
        {multiline ? (
          <textarea
            id={id ?? name}
            name={name}
            className={classes}
            ref={ref as ForwardedRef<HTMLTextAreaElement>}
            {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}></textarea>
        ) : (
          <input
            id={id ?? name}
            name={name}
            className={classes}
            ref={ref as ForwardedRef<HTMLInputElement>}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
          />
        )}
        <FieldError error={error} />
      </div>
    )
  }
)

Input.defaultProps = {
  variant: 'base',
}

export default Input
